import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import GlobalFilterBar from "../../components/ui/GlobalFilterBar";
import DataEntryForm from "./components/DataEntryForm";
import TodaysSummary from "./components/TodaysSummary";
import EntryProgressTracker from "./components/EntryProgressTracker";
import RecentSubmissionsTable from "./components/RecentSubmissionsTable";
import BulkImportSection from "./components/BulkImportSection";
import OfflineIndicator from "./components/OfflineIndicator";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { supabase } from "utils/supabase";
import AllSubmissionsTable from "./components/AllSubmissionsTable";

const DataEntryAndValidationInterface = () => {
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  const [allEntries, setAllEntries] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [companyOptions, setCompanyOptions] = useState([
    { value: "all", label: "All Companies" },
  ]);

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setCurrentUser(user);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!currentUser?.id) return;

      // Fetch user profile (to get role and assigned companies)
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("role, user_companies")
        .eq("id", currentUser.id)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }

      let companies = [];

      if (profile.role === "admin") {
        // Admin: fetch all companies
        const { data, error } = await supabase
          .from("companies")
          .select("id, name, code")
          .order("name", { ascending: true });

        if (error) {
          console.error("Error fetching all companies:", error);
          return;
        }

        companies = data;
      } else {
        // Regular user: fetch only companies in user_companies array
        const companyIds = profile.user_companies;

        if (!companyIds || companyIds.length === 0) {
          console.warn("No assigned companies for this user.");
          return;
        }

        const { data, error } = await supabase
          .from("companies")
          .select("id, name, code")
          .in("id", companyIds);

        if (error) {
          console.error("Error fetching assigned companies:", error);
          return;
        }
        // console.log("user ", data);

        companies = data;

        // console.log("company", companies);
      }

      const options = companies.map((company) => ({
        value: company.id,
        label: `${company.name} (${company.code})`,
      }));
      // console.log("options", options);

      setCompanyOptions([{ value: "all", label: "All Companies" }, ...options]);
    };

    fetchCompanies();
  }, [currentUser]);

  // console.log("companyOptions", companyOptions);

  useEffect(() => {
    const fetchAllEntries = async () => {
      if (!currentUser?.id) return;

      // Step 1: Fetch user role
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("role")
        .eq("id", currentUser.id)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }

      const isAdmin = profile.role === "admin";

      // Step 2: Build query with join to user_profiles
      const query = supabase
        .from("sales_entries")
        .select("*, user_profiles ( full_name )"); // Join to get user's name

      if (!isAdmin) {
        query.eq("user_id", currentUser.id);
      }

      // Step 3: Execute the query
      const { data, error } = await query;

      if (error) {
        console.error("Error fetching entries:", error);
        return;
      }

      // Optional: flatten the nested user name if needed
      const withNames = data.map((entry) => ({
        ...entry,
        user_name: entry.user_profiles?.full_name || "Unknown",
      }));

      setAllEntries(withNames);
    };

    fetchAllEntries();
  }, [currentUser]);

  // Load entries from localStorage on component mount
  useEffect(() => {
    const savedEntries = localStorage.getItem("salesEntries");
    const savedPendingEntries = localStorage.getItem("pendingSalesEntries");

    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }

    if (savedPendingEntries) {
      setPendingEntries(JSON.parse(savedPendingEntries));
    }
  }, []);

  // Save entries to localStorage whenever entries change
  useEffect(() => {
    localStorage.setItem("salesEntries", JSON.stringify(entries));
  }, [entries]);

  // Save pending entries to localStorage
  useEffect(() => {
    localStorage.setItem("pendingSalesEntries", JSON.stringify(pendingEntries));
  }, [pendingEntries]);

  // Get today's entries
  const todaysEntries = entries.filter((entry) => {
    const entryDate = new Date(entry.entry_date).toDateString();
    const today = new Date().toDateString();
    return entryDate === today;
  });

  const handleSubmitEntry = async (entryData) => {
    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (editingEntry) {
        // Update existing entry
        setEntries((prev) =>
          prev.map((entry) =>
            entry.id === editingEntry.id ? entryData : entry
          )
        );
        setAllEntries((prev) =>
          prev.map((entry) =>
            entry.id === editingEntry.id ? entryData : entry
          )
        );
        setEditingEntry(null);
      } else {
        // Add new entry
        setEntries((prev) => [...prev, entryData]);
        setAllEntries((prev) => [...prev, entryData]);
      }

      // Show success notification (in a real app, you'd use a toast library)
      // console.log("Entry submitted successfully:", entryData);
    } catch (error) {
      console.error("Failed to submit entry:", error);
      throw error;
    }
  };

  const handleAutoSave = (formData) => {
    if (!autoSaveEnabled) return;

    // Save to pending entries for offline support
    const autoSaveEntry = {
      ...formData,
      id: `autosave-${Date.now()}`,
      isAutoSave: true,
      timestamp: new Date().toISOString(),
    };

    setPendingEntries((prev) => {
      const filtered = prev.filter((entry) => !entry.isAutoSave);
      return [...filtered, autoSaveEntry];
    });
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteEntry = (entryId) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
      setAllEntries((prev) => prev.filter((entry) => entry.id !== entryId));
    }
  };

  const deleteEntry = async (entryId) => {
    const { error } = await supabase
      .from("sales_entries")
      .delete()
      .eq("id", entryId);

    if (error) {
      console.error("Error deleting entry:", error);
      return false;
    }

    console.log("Entry deleted successfully");
    return true;
  };

  const handleEntryDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this entry?"
    );
    if (!confirmed) return;

    const success = await deleteEntry(id);
    if (success) {
      setAllEntries((prev) => prev.filter((entry) => entry.id !== id));
      setEntries((prev) => prev.filter((entry) => entry.id !== id));
    }
  };

  const handleBulkImport = async (importData) => {
    try {
      // Process bulk import data
      const processedEntries = importData.map((item) => ({
        ...item,
        id: `bulk-${Date.now()}-${Math.random()}`,
        timestamp: new Date().toISOString(),
        collectionRate:
          item.salesAmount > 0
            ? (item.collectionAmount / item.salesAmount) * 100
            : 0,
      }));

      setEntries((prev) => [...prev, ...processedEntries]);

      // Show success message
      console.log(`Successfully imported ${processedEntries.length} entries`);
    } catch (error) {
      console.error("Bulk import failed:", error);
      throw error;
    }
  };

  const handleSyncPendingEntries = async () => {
    try {
      // Simulate sync process
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Move pending entries to main entries
      const validPendingEntries = pendingEntries.filter(
        (entry) => !entry.isAutoSave
      );
      setEntries((prev) => [...prev, ...validPendingEntries]);
      setPendingEntries([]);

      console.log("Sync completed successfully");
    } catch (error) {
      console.error("Sync failed:", error);
      throw error;
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar />

      <div className="container mx-auto px-2 pt-20 sm:pt-20 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8">
          <div className="flex flex-col ">
            <h1 className="text-xl sm:text-3xl text-center sm:text-left font-bold text-foreground">
              Data Entry & Validation
            </h1>
            <p className="text-muted-foreground text-center mt-2">
              Streamlined daily sales figure submission with comprehensive
              validation
            </p>
          </div>
          <div className="flex flex-col  sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkImport(!showBulkImport)}
              iconName="Upload"
              iconPosition="left"
            >
              Bulk Import
            </Button>
            <Button
              variant="ghost"
              onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
              iconName={autoSaveEnabled ? "Save" : "SaveOff"}
              iconPosition="left"
              className={
                autoSaveEnabled ? "text-success" : "text-muted-foreground"
              }
            >
              Auto-save {autoSaveEnabled ? "On" : "Off"}
            </Button>
          </div>
        </div>

        {/* Offline Indicator */}
        <div className="mb-6">
          <OfflineIndicator
            companyOptions={companyOptions}
            pendingEntries={pendingEntries}
            onSync={handleSyncPendingEntries}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Panel - Data Entry Form */}
          <div className="lg:col-span-8">
            <div className="space-y-6">
              {/* Edit Mode Banner */}
              {editingEntry && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Edit2" size={16} className="text-warning" />
                      <span className="text-sm font-medium text-warning">
                        Editing Entry - {editingEntry.company}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      iconName="X"
                      className="h-8 w-8 p-0"
                    />
                  </div>
                </div>
              )}

              {/* Data Entry Form */}
              <DataEntryForm
                onSubmit={handleSubmitEntry}
                onAutoSave={handleAutoSave}
                editingEntry={editingEntry}
                companies={companyOptions}
              />

              {/* Bulk Import Section */}
              {showBulkImport && (
                <BulkImportSection onBulkImport={handleBulkImport} />
              )}
            </div>
          </div>

          {/* Right Panel - Summary and Progress */}
          <div className="lg:col-span-4">
            <div className="space-y-6">
              {/* Today's Summary */}
              <TodaysSummary
                companyOptions={companyOptions}
                entries={todaysEntries}
                onEdit={handleEditEntry}
                onDelete={handleDeleteEntry}
              />

              {/* Entry Progress Tracker */}
              {/* <EntryProgressTracker entries={todaysEntries} /> */}
            </div>
          </div>
        </div>

        {/* Recent Submissions Table */}
        <div className="mt-8">
          <RecentSubmissionsTable
            companyOptions={companyOptions}
            submissions={entries}
            onEdit={handleEditEntry}
            onDelete={handleDeleteEntry}
          />
        </div>
        <div className="mt-8">
          <AllSubmissionsTable
            companyOptions={companyOptions}
            submissions={allEntries}
            onEdit={handleEditEntry}
            onDelete={handleEntryDelete}
          />
        </div>

        {/* Quick Actions Footer */}
        <div className="mt-8 p-4 bg-muted/50 rounded-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            <div className="text-sm text-muted-foreground">
              <Icon name="Keyboard" size={16} className="inline mr-2" />
              Keyboard shortcuts: Ctrl+S to save, Ctrl+D to duplicate
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Shield" size={14} />
              <span>All data is encrypted and securely stored</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataEntryAndValidationInterface;
